/**
 * VRInteractComponent - Handles simple interaction (trigger press) with hovered VRInteractableComponent actors.
 */


import { SceneComponent } from '../../components/SceneComponent.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';
import { VRInteractableComponent } from '../actor-components/VRInteractableComponent.js';
import { XRButton } from '../VRControllerManager.js';
import { VRPawn } from '../VRPawn.js';

import type { SceneComponentOptions } from '../../components/SceneComponent.js';
import type { IVRInputHandler } from '../VRPawn.js';


export interface VRInteractOptions extends SceneComponentOptions {
  button?: XRButton;
  handedness?: 'left' | 'right' | 'any';
}

@EngineClass('VRInteractComponent')
export class VRInteractComponent extends SceneComponent implements IVRInputHandler {
  @property()
  public button: XRButton = XRButton.Trigger;

  @property()
  public handedness: 'left' | 'right' | 'any' = 'any';


  private pawn: VRPawn | null = null;

  public override initialize(options?: VRInteractOptions): void {
    super.initialize(options);
    this.button = options?.button ?? this.button;
    this.handedness = options?.handedness ?? this.handedness;
  }



  public override beginPlay(): void {
    super.beginPlay();
    const actor = this.getActor();
    if (actor instanceof VRPawn) {
      this.pawn = actor;
      this.pawn.addVRInputHandler(this);
    }
  }

  public override endPlay(): void {
    if (this.pawn) this.pawn.removeVRInputHandler(this);
    this.pawn = null;
    super.endPlay();
  }

  public handleVRButtonDown(inputSource: XRInputSource, button: XRButton, _value: number): boolean {
    const pawn = this.pawn; if (!pawn) return false;
    const hand = inputSource.handedness as 'left' | 'right' | 'none';
    if (hand === 'none') return false;

    if (this.handedness !== 'any' && hand !== this.handedness) return false;

    const target = pawn.getHoverTarget(hand as 'left' | 'right');
    if (!target) return false;

    const actor = target.actor;
    const comp = actor.getComponent(VRInteractableComponent) ?? null;

    const requiredButton = comp ? comp.getVRInteractionButton() : this.button;
    if (button !== requiredButton) return false;

    comp?.onInteract.invoke(hand);
    return true;
  }
}


